<template>
  <div class="books-page">

    <section class="articles" :class="{ 'books-hero--en': !isAr }">
      <b-container fluid class="px-0">
        <b-row
          no-gutters
          class="books-hero-split intro-section align-items-stretch mx-0"
          :class="{ 'flex-xl-row-reverse': isAr }"
        >
          <b-col
            cols="12"
            xl="6"
            class="books-hero-copy d-flex align-items-center py-5 px-4 px-xl-5"
          >
            <div class="books-hero-text w-100">
              <h1 class="mb-3"><b>{{ langDec.books_hero_title }}</b></h1>
              <p class="mb-0">{{ langDec.books_hero_subtitle }}</p>
            </div>
          </b-col>
          <b-col cols="12" xl="6" class="books-hero-art p-0" aria-hidden="true" />
        </b-row>
      </b-container>
    </section>

    <section class="section93">
      <b-container fluid>
        <b-row>
          <b-col xl="12" lg="12" md="12" sm="12">
            <div class="text-section books-section-inner">
              <div>
                <div class="title books-section-title">
                  <h2 class="name mb-4">
                    {{ langDec.books_latest }}
                  </h2>
                </div>

                <div v-if="pageIsLoading" class="books-loading text-center py-5">
                  <b-spinner variant="primary" :label="langDec.books_loading"></b-spinner>
                  <p class="mt-3 mb-0 text-muted">{{ langDec.books_loading }}</p>
                </div>

                <b-row v-else class="books-grid">
                  <b-col
                    v-for="book in items"
                    :key="bookKey(book)"
                    cols="12"
                    class="books-grid__col mb-4"
                  >
                    <article class="book-card book-card--wide transBox h-100">
                      <div
                        v-if="book.url"
                        role="button"
                        tabindex="0"
                        class="book-card__media-link"
                        @click="openBookViewer(book)"
                        @keyup.enter="openBookViewer(book)"
                      >
                        <div class="book-card__media">
                          <img
                            class="book-card__img gridSection-img"
                            :src="bookCoverSrc(book)"
                            :alt="book.title || langDec.books_alt_cover"
                            loading="lazy"
                          />
                        </div>
                      </div>
                      <div v-else class="book-card__media">
                        <img
                          class="book-card__img gridSection-img"
                          :src="bookCoverSrc(book)"
                          :alt="book.title || langDec.books_alt_cover"
                          loading="lazy"
                        />
                      </div>

                      <div class="book-card__body transBox-text">
                        <div v-if="book.specialization && book.specialization.name" class="book-card__badge">
                          {{ book.specialization.name }}
                        </div>
                        <h3 class="book-card__title">
                          {{ book.title }}
                        </h3>
                        <p class="book-card__desc transBox-p">
                          {{ book.description }}
                        </p>

                        <div v-if="book.publishDate" class="book-card__date">
                          {{ formatBookDate(book.publishDate) }}
                        </div>

                        <div v-if="book.doctor" class="book-card__doctor">
                          <img
                            v-if="book.doctor.imageUrl"
                            class="book-card__doctor-avatar"
                            :src="doctorImageSrc(book.doctor)"
                            alt=""
                          />
                          <div class="book-card__doctor-meta">
                            <span class="book-card__doctor-label">{{ langDec.books_doctor_label }}</span>
                            <span
                              class="book-card__doctor-name"
                              :class="{ 'book-card__doctor-name--link': Boolean(doctorProfileIdForBook(book)) }"
                              :role="doctorProfileIdForBook(book) ? 'button' : null"
                              :tabindex="doctorProfileIdForBook(book) ? 0 : null"
                              @click.stop="onDoctorNameClick(book)"
                              @keyup.enter.stop="onDoctorNameClick(book)"
                            >{{ book.doctor.name }}</span>
                            <span v-if="book.doctor.job" class="book-card__doctor-job">{{ book.doctor.job }}</span>
                          </div>
                        </div>

                        <div v-if="book.url" class="book-card__actions">
                          <b-button
                            variant="primary"
                            size="sm"
                            class="book-card__cta"
                            @click="openBookViewer(book)"
                          >
                            {{ langDec.books_cta_open }}
                          </b-button>
                        </div>
                      </div>
                    </article>
                  </b-col>
                </b-row>

                <div v-if="!pageIsLoading && items.length === 0" class="text-center text-muted py-5">
                  {{ langDec.books_empty }}
                </div>
              </div>
            </div>
          </b-col>
        </b-row>
      </b-container>
    </section>

    <b-modal
      v-model="bookModalShow"
      size="xl"
      scrollable
      centered
      :hide-header="true"
      :hide-footer="true"
      body-class="p-0"
      content-class="book-viewer-bv-content"
      dialog-class="book-viewer-dialog-wrap"
      modal-class="book-viewer-modal-root"
      @hidden="onBookModalHidden"
    >
      <div v-if="modalBook" class="book-viewer-shell" :dir="isAr ? 'rtl' : 'ltr'">
        <header class="book-viewer-top">
          <div class="book-viewer-top__title-wrap">
            <h4 class="book-viewer-title">{{ bookModalTitle }}</h4>
            <span v-if="modalIsPdf" class="book-viewer-badge">PDF</span>
            <span v-else class="book-viewer-badge book-viewer-badge--muted">{{ langDec.books_viewer_external_link }}</span>
          </div>
          <div class="book-viewer-actions">
            <b-button
              variant="light"
              size="sm"
              class="book-viewer-close"
              :aria-label="langDec.books_viewer_close"
              @click="bookModalShow = false"
            >
              <b-icon icon="x-lg" aria-hidden="true" />
            </b-button>
          </div>
        </header>

        <div class="book-viewer-body">
          <template v-if="modalIsPdf">
            <div class="book-viewer-frame-wrap">
              <iframe
                :key="pdfFrameKey + '-' + pdfEmbedMode"
                ref="pdfIframe"
                class="book-viewer-iframe"
                :title="langDec.books_viewer_pdf_title"
                :src="modalPdfIframeSrc"
              />
            </div>
            <div class="book-viewer-pdf-toolbar">
              <b-button variant="primary" size="sm" class="font-weight-bold" @click="openBookInNewTab">
                {{ langDec.books_open_new_tab }}
              </b-button>
              <b-button variant="outline-secondary" size="sm" @click="togglePdfEmbedMode">
                {{ pdfEmbedMode === 'google' ? langDec.books_toggle_to_direct : langDec.books_toggle_to_google }}
              </b-button>
            </div>
            <p class="book-viewer-hint">
              {{ langDec.books_viewer_hint }}
            </p>
          </template>
          <div v-else class="book-viewer-fallback">
            <b-icon icon="box-arrow-up-right" font-scale="2" class="book-viewer-fallback-icon" />
            <p class="book-viewer-fallback-text">
              {{ langDec.books_viewer_fallback_text }}
            </p>
            <b-button variant="primary" size="lg" class="px-4" @click="openBookInNewTab">
              {{ langDec.books_open_link }}
            </b-button>
          </div>
        </div>
      </div>
    </b-modal>

    <section class="subscribe">
      <div>
        <div>
          <div class="">
            <div class="col-md-12 col-sm-12 col-xs-12 books-subscribe-outer">
              <div class="py-5 my-5">
                <div class="">
                  <h2>{{ langDec.articles_subscribe_title }}</h2>
                  <div class="description">
                    <br>
                    <p v-if="!googleplay && !applestore" style="color: red;">
                      <b>{{ langDec.articles_app_unavailable }}</b>
                      <br>
                      <b-button @click="goToRoute('contactus')" class="px-4 py-2 my-4" variant="primary">
                        <span>{{ langDec.articles_contact_us }}</span>
                      </b-button>
                    </p>
                  </div>
                  <div v-if="googleplay || applestore" class="action-btns mt-2">
                    <b-button v-if="applestore" @click="goTowebsite(applestore)" class="px-4 py-3 mx-4 my-3"
                      style="background-color: black; color:white !important; border: black !important;">
                      <div
                        class="store-badge-inner carousel-btn-in-div px-3"
                        :class="{ 'store-badge-inner--en': !isAr }"
                      >
                        <div class="store-badge-inner__icon store-badge-inner__icon--apple">
                          <img class="store-badge-inner__apple-img"
                            src="../assets/images/Apple_logo_white.svg.png" alt="">
                        </div>
                        <div class="carousel-btn-in-div-text store-badge-inner__text books-subscribe-store-text">
                          <span>{{ langDec.nav_available_on }}</span>
                          <br>
                          <span>App Store</span>
                        </div>
                      </div>
                    </b-button>
                    <b-button v-if="googleplay" @click="goTowebsite(googleplay)" class="px-4 py-3 mx-4 my-3"
                      variant="primary" style="color:white !important;border: black !important;">
                      <div
                        class="store-badge-inner px-3"
                        :class="{ 'store-badge-inner--en': !isAr }"
                      >
                        <div class="store-badge-inner__icon store-badge-inner__icon--google">
                          <svg class="kOqhQd" aria-hidden="true" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
                            <path fill="none" d="M0,0h40v40H0V0z"></path>
                            <g>
                              <path
                                d="M19.7,19.2L4.3,35.3c0,0,0,0,0,0c0.5,1.7,2.1,3,4,3c0.8,0,1.5-0.2,2.1-0.6l0,0l17.4-9.9L19.7,19.2z"
                                fill="#EA4335"></path>
                              <path
                                d="M35.3,16.4L35.3,16.4l-7.5-4.3l-8.4,7.4l8.5,8.3l7.5-4.2c1.3-0.7,2.2-2.1,2.2-3.6C37.5,18.5,36.6,17.1,35.3,16.4z"
                                fill="#FBBC04"></path>
                              <path d="M4.3,4.7C4.2,5,4.2,5.4,4.2,5.8v28.5c0,0.4,0,0.7,0.1,1.1l16-15.7L4.3,4.7z"
                                fill="#4285F4"></path>
                              <path
                                d="M19.8,20l8-7.9L10.5,2.3C9.9,1.9,9.1,1.7,8.3,1.7c-1.9,0-3.6,1.3-4,3c0,0,0,0,0,0L19.8,20z"
                                fill="#34A853"></path>
                            </g>
                          </svg>
                        </div>
                        <div class="carousel-btn-in-div-text store-badge-inner__text books-subscribe-store-text">
                          <span>{{ langDec.nav_available_on }}</span>
                          <br>
                          <span>Google Play</span>
                        </div>
                      </div>
                    </b-button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

  </div>
</template>

<script>
import { mapGetters } from "vuex"
import { sendRequest } from '../utils/RequestHelper.js'
import { resolveDoctorProfileIdFromBook } from '../utils/doctorProfileNav.js'

export default {
  name: 'Home',
  data() {
    return {
      items: [],
      pageIsLoading: false,
      googleplay: process.env.VUE_APP_GOOGLEPLAYLINK,
      applestore: process.env.VUE_APP_APPLESTORELINK,
      bookModalShow: false,
      modalBook: null,
      pdfFrameKey: 0,
      /** google: iframe يحمّل عبر Google Viewer (أقل مشاكل مع سيرفرات المجلات)؛ direct: الرابط الأصلي */
      pdfEmbedMode: 'google',
    }
  },
  computed: {
    ...mapGetters(["getLangData", "getlang"]),
    langDec() {
      return this.getLangData || {}
    },
    isAr() {
      return this.getlang === "ar"
    },
    bookModalTitle() {
      const fb = (this.getLangData && this.getLangData.books_placeholder_title) || 'Book'
      return (this.modalBook && this.modalBook.title) ? this.modalBook.title : fb
    },
    modalIsPdf() {
      const url = this.modalBook && this.modalBook.url;
      return this.isLikelyPdfUrl(url);
    },
    modalPdfIframeSrc() {
      const url = this.modalBook && this.modalBook.url;
      if (!url || !this.modalIsPdf) return '';
      if (this.pdfEmbedMode === 'direct') {
        return url;
      }
      try {
        return `https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`;
      } catch {
        return url;
      }
    },
  },
  methods: {
    apiImageBase() {
      const b = process.env.VUE_APP_API_URL_IMAGE || '';
      return b.replace(/\/?$/, '/');
    },
    bookKey(book) {
      return book.bookId || book.id || book.title || Math.random();
    },
    bookCoverSrc(book) {
      const path = book && book.imageUrl;
      if (!path) {
        const ph = (this.getLangData && this.getLangData.books_placeholder_title) || 'Book'
        return 'data:image/svg+xml,' + encodeURIComponent(
          `<svg xmlns="http://www.w3.org/2000/svg" width="400" height="300" viewBox="0 0 400 300"><rect fill="#e8f5ef" width="400" height="300"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="#006633" font-family="sans-serif" font-size="18">${ph}</text></svg>`
        );
      }
      if (/^https?:\/\//i.test(path)) return path;
      return this.apiImageBase() + String(path).replace(/^\//, '');
    },
    doctorImageSrc(doctor) {
      if (!doctor || !doctor.imageUrl) return '';
      const path = doctor.imageUrl;
      if (/^https?:\/\//i.test(path)) return path;
      return this.apiImageBase() + String(path).replace(/^\//, '');
    },
    formatBookDate(raw) {
      if (!raw) return '';
      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) return String(raw);
      const loc = this.getlang === 'ar' ? 'ar-EG' : 'en-US'
      return d.toLocaleDateString(loc, { year: 'numeric', month: 'long', day: 'numeric' });
    },
    isLikelyPdfUrl(url) {
      if (!url || typeof url !== 'string') return false;
      const u = url.split('#')[0].split('?')[0].toLowerCase();
      return u.endsWith('.pdf');
    },
    openBookViewer(book) {
      if (!book || !book.url) return;
      this.modalBook = { ...book };
      this.pdfEmbedMode = 'google';
      this.pdfFrameKey += 1;
      this.bookModalShow = true;
    },
    onBookModalHidden() {
      this.modalBook = null;
      this.pdfEmbedMode = 'google';
    },
    togglePdfEmbedMode() {
      this.pdfEmbedMode = this.pdfEmbedMode === 'google' ? 'direct' : 'google';
      this.pdfFrameKey += 1;
    },
    openBookInNewTab() {
      const url = this.modalBook && this.modalBook.url;
      if (!url) return;
      window.open(url, '_blank', 'noopener,noreferrer');
    },
    goTowebsite(link) {
      window.open(link, "_blank");
    },
    goToRoute(name, id) {
      this.$router.push({
        name,
        id
      });
    },
    doctorProfileIdForBook(book) {
      return resolveDoctorProfileIdFromBook(book)
    },
    onDoctorNameClick(book) {
      const id = resolveDoctorProfileIdFromBook(book)
      if (!id) return
      this.$router.push({
        name: 'expertProfile',
        params: { id: String(id) },
      })
    },
    loadData() {
      this.pageIsLoading = true;
      const body = {
        displayedOnly: 'true',
        pageNumber: 1,
        pageSize: 20
      };
      sendRequest('post', 'book/Search', true, body,
        (response) => {
          const pack = response && response.data && response.data.data;
          const list = pack && pack.items;
          this.items = Array.isArray(list) ? list : [];
          this.pageIsLoading = false;
        },
        () => {
          this.pageIsLoading = false;
          this.items = [];
        }
      );
    }

  },
  created() {
    this.loadData();
  }
}
</script>

<style scoped>
/* Split hero */
.articles .books-hero-split {
  min-height: 450px;
}

.books-hero-art {
  min-height: 280px;
  background-color: #0d7a45;
  background-image: url(../assets/images/booksBG.png);
  background-size: cover;
  background-repeat: no-repeat;
  background-position: left center;
}

.articles:not(.books-hero--en) .books-hero-art {
  background-size: contain;
  background-position: center center;
}

.books-hero-copy {
  background: linear-gradient(160deg, #054d2a 0%, #006633 42%, #0a7a44 100%);
  color: #fff;
}

.books-hero-text {
  text-align: start;
}

.books-hero-text h1 {
  font-size: 2.25rem !important;
  font-weight: bolder !important;
  line-height: 1.2;
  color: #fff !important;
}

.books-hero-text p {
  font-size: 1.125rem;
  line-height: 1.55;
  color: rgba(255, 255, 255, 0.96) !important;
}

@media (min-width: 1200px) {
  .books-hero-art {
    min-height: 450px;
  }

  .books-hero-text h1 {
    font-size: 2.5rem !important;
  }

  .books-hero-text p {
    font-size: 1.2rem;
  }
}

@media (max-width: 991.98px) {
  .articles .books-hero-split {
    min-height: 0;
  }

  .books-hero-art {
    min-height: 260px;
  }
}

@media (max-width: 767.98px) {
  .books-hero-art {
    min-height: 220px;
  }

  .books-hero-text h1 {
    font-size: 1.75rem !important;
  }
}

.books-section-title {
  text-align: start;
}

.books-section-inner {
  width: 100%;
}

.books-grid {
  margin-top: 0.25rem;
}

.books-grid__col {
  display: flex;
  flex: 0 0 100%;
  max-width: 100%;
  width: 100%;
}

.book-card {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100%;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid rgba(0, 102, 51, 0.14);
  box-shadow: 0 10px 32px rgba(0, 102, 51, 0.08);
  background: #fff;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.book-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 16px 44px rgba(0, 102, 51, 0.12);
}

@media (min-width: 768px) {
  .book-card--wide {
    flex-direction: row;
    align-items: stretch;
  }

  .book-card--wide > .book-card__media-link,
  .book-card--wide > .book-card__media {
    width: min(280px, 34vw);
    flex-shrink: 0;
    align-self: stretch;
  }

  .book-card--wide > .book-card__media-link {
    display: flex;
  }

  .book-card--wide .book-card__media {
    flex: 1;
    aspect-ratio: auto;
    min-height: 220px;
  }

  .book-card--wide .book-card__img {
    height: 100%;
    min-height: 220px;
    object-fit: cover;
  }

  .book-card--wide .book-card__body {
    flex: 1;
    min-width: 0;
  }
}

.book-card__media-link {
  display: block;
  text-decoration: none !important;
  color: inherit;
  cursor: pointer;
}

.book-card__media {
  position: relative;
  aspect-ratio: 4 / 3;
  background: linear-gradient(145deg, #f0faf4 0%, #e2efe8 100%);
  overflow: hidden;
}

.book-card__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  border-radius: 0 !important;
}

.book-card__body {
  padding: 1rem 1.1rem 1.15rem !important;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.book-card__badge {
  display: inline-block;
  align-self: flex-start;
  font-size: 0.72rem;
  font-weight: 700;
  padding: 0.28rem 0.65rem;
  border-radius: 999px;
  background: rgba(0, 102, 51, 0.1);
  color: #006633;
  border: 1px solid rgba(0, 102, 51, 0.2);
  margin-bottom: 0.65rem;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.book-card__title {
  font-size: 1.02rem;
  font-weight: 800;
  line-height: 1.45;
  color: #1a1d1f;
  margin: 0 0 0.55rem;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.book-card__desc {
  margin: 0 0 0.75rem !important;
  font-size: 0.9rem;
  line-height: 1.65;
  color: #4a5560;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.book-card__date {
  font-size: 0.8rem;
  color: #6c757d;
  margin-bottom: 0.75rem;
}

.book-card__doctor {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding-top: 0.75rem;
  margin-top: auto;
  border-top: 1px solid rgba(0, 102, 51, 0.12);
}

.book-card__doctor-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  border: 2px solid rgba(0, 102, 51, 0.2);
}

.book-card__doctor-meta {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: right;
  min-width: 0;
}

.book-card__doctor-label {
  font-size: 0.68rem;
  font-weight: 700;
  color: rgba(0, 102, 51, 0.75);
  text-transform: none;
}

.book-card__doctor-name {
  font-size: 0.88rem;
  font-weight: 700;
  color: #2d3436;
  word-break: break-word;
}

.book-card__doctor-name--link {
  color: #006633;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 3px;
}

.book-card__doctor-name--link:hover,
.book-card__doctor-name--link:focus {
  color: #004d26;
  outline: none;
}

.book-card__doctor-job {
  font-size: 0.78rem;
  color: #6c757d;
}

.book-card__actions {
  margin-top: 1rem;
}

.book-card__cta {
  border-radius: 10px !important;
  font-weight: 700 !important;
  background-color: #006633 !important;
  border-color: #006633 !important;
}

@media (min-width: 768px) {
  .book-card--wide > .book-card__media-link {
    display: flex;
  }
}

/* —— مودال عارض الكتاب —— */
::v-deep .book-viewer-modal-root .modal-dialog {
  max-width: min(1120px, 96vw);
  margin: 1rem auto;
}

::v-deep .book-viewer-bv-content {
  border: none;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.35);
}

.book-viewer-shell {
  background: linear-gradient(180deg, #f6faf7 0%, #eef5f0 100%);
  min-height: 200px;
  text-align: right;
}

.book-viewer-top {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 1rem 1.15rem;
  background: linear-gradient(135deg, #006633 0%, #0d8049 100%);
  color: #fff;
}

.book-viewer-top__title-wrap {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
  min-width: 0;
}

.book-viewer-title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 800;
  line-height: 1.4;
  color: #fff !important;
}

.book-viewer-badge {
  font-size: 0.68rem;
  font-weight: 700;
  padding: 0.2rem 0.5rem;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.22);
  color: #fff;
}

.book-viewer-badge--muted {
  background: rgba(0, 0, 0, 0.2);
}

.book-viewer-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.45rem;
  flex-shrink: 0;
}

.book-viewer-close {
  border-radius: 10px !important;
  padding: 0.35rem 0.55rem !important;
  line-height: 1 !important;
}

.book-viewer-body {
  background: #f0f4f2;
}

.book-viewer-frame-wrap {
  background: #2d3338;
  min-height: min(72vh, 640px);
}

.book-viewer-iframe {
  display: block;
  width: 100%;
  height: min(72vh, 640px);
  border: 0;
  background: #1c2127;
}

.book-viewer-pdf-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-start;
  gap: 0.5rem;
  padding: 0.65rem 1rem;
  background: #e8efea;
  border-top: 1px solid rgba(0, 102, 51, 0.12);
}

.book-viewer-hint {
  margin: 0;
  padding: 0.6rem 1rem;
  font-size: 0.82rem;
  color: #5a6570;
  background: #e8efea;
  border-top: 1px solid rgba(0, 102, 51, 0.12);
}

.book-viewer-fallback {
  padding: 2.5rem 1.5rem;
  text-align: center;
  background: #f6faf7;
}

.book-viewer-fallback-icon {
  color: #006633;
  opacity: 0.75;
}

.book-viewer-fallback-text {
  max-width: 420px;
  margin: 0 auto 1.25rem;
  font-size: 1rem;
  color: #495057;
  line-height: 1.65;
}

html[data-theme="dark"] .book-viewer-shell {
  background: #161b22;
}

html[data-theme="dark"] .book-viewer-body {
  background: #0d1117;
}

html[data-theme="dark"] .book-viewer-frame-wrap {
  background: #010409;
}

html[data-theme="dark"] .book-viewer-pdf-toolbar {
  background: #21262d;
  border-top-color: #30363d;
}

html[data-theme="dark"] .book-viewer-hint {
  background: #161b22;
  color: #9aa4b2;
  border-top-color: #30363d;
}

html[data-theme="dark"] .book-viewer-fallback {
  background: #161b22;
}

html[data-theme="dark"] .book-viewer-fallback-text {
  color: #cfd6df;
}

.books-subscribe-outer {
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.store-badge-inner {
  display: flex;
  align-items: center;
  justify-content: center;
  direction: ltr;
  gap: 0.875rem;
}

.store-badge-inner--en {
  flex-direction: row-reverse;
}

.store-badge-inner__icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.store-badge-inner__icon--apple {
  width: 35px;
  height: 35px;
  color: #fff;
}

.store-badge-inner__icon--google {
  width: 40px;
  height: 40px;
}

.store-badge-inner__apple-img {
  width: 35px;
  height: auto;
  margin-top: -3px;
  display: block;
}

.books-subscribe-store-text {
  font-size: 14px;
  line-height: 1.25;
  text-align: start;
}

.greyBox {
  padding: 50px 40px;
}

.greyBox .text-section {
  background-color: #f5f5f5;
  border-radius: 10px;
  padding: 20px 30px;
  font-size: 20px;
  width: 100%;
}

.greyBox .text-section .title-green {
  color: #006633;
  font-size: 24px;
}

.greyBox .text-section .name {
  font-size: 32px;
  margin-top: 20px;
  margin-bottom: 30px;
  font-weight: bold;
}

.greyBox .text-section .pref {
  font-size: 16px;
  color: #858585;
}

.subscribe {
  background-color: #f5f5f5;
}

.section93 {
  padding: 40px 40px;
}

.section93 .itemBox {
  border: 1px solid #36A9E1;
  padding: 15px;
  border-radius: 10px;
  margin-top: 30px;
  margin-bottom: 30px;
}

.filterChBox .btn-group-toggle {
  display: block;
}

.order2 {
  order: 1;
}

.order1 {
  order: 2;
}

@media (max-width: 991.98px) {

  .greyBox,
  .section93 {
    padding: 50px 30px;
  }

  .text-section h2 {
    font-size: 30px !important;
    font-weight: bolder !important;
  }

  .button-section .btn {
    font-size: 10px !important;
  }

  .greyBox .text-section {
    padding: 10px;
  }
}

@media (max-width: 767.98px) {

  .greyBox,
  .section93 {
    padding: 40px 10px;
  }
}
</style>
